/**
 * What the room made of itself, what it set out to learn, and who it gave
 * work to.
 *
 * The dig used to be a budget bar and eight seats saying "working". Nothing
 * said what the room thought it was DOING, so a run that spent four million
 * tokens and moved no table looked exactly like one that had worked well.
 *
 * TWO SIZES. Above the board it is ONE LINE with a way in (`DirectorLine`): the
 * full read filled a third of the screen and pushed the tables it was about
 * out of sight. The whole of it is the ROOM BRIEF (`DirectorNote`), a slideout
 * of cards that each answer one question, in the order you would ask them.
 */
import type { ReactNode } from 'react';
import type { BoardroomAssignment, BoardroomResearchPlan, BoardroomRun } from '../../../types';
import {
  assignmentLine,
  directorHeadline,
  gapWords,
  groupAssignments,
  noteIsStale,
  planTally,
  readyPlan,
  roundsLabel,
} from './directorView';
import HeaderRow, { Door } from './HeaderRow';
import { seatRole } from '../plainWords';

/** The line above the board: the gist, what is worth a look, and the way in. */
export function DirectorLine({
  run,
  assignments,
  plan,
  frameWaiting = 0,
  onOpen,
}: {
  run: BoardroomRun | null;
  assignments: BoardroomAssignment[];
  /** The room's latest plan, when the run has none of its own. */
  plan?: BoardroomResearchPlan | null;
  /** Frame proposals waiting on a yes or a no — they live in the slideout. */
  frameWaiting?: number;
  onOpen: () => void;
}) {
  // ALWAYS DRAWN. It is the one way into the room brief, and the brief is also
  // where the room is steered — so a room with nothing to report still needs
  // the door.
  const head = directorHeadline(run, assignments, frameWaiting, plan) ?? {
    gist: 'the setup, the focus, and what the next dig should chase',
    chips: [],
  };
  // A ROW OF THE HEADER CARD, the answer's sibling: the same label column, the
  // same whole-row door. It used to be its own box with its door at the far
  // right edge, a screen's width from the gist it opened.
  return (
    <HeaderRow
      label="Doing"
      onOpen={onOpen}
      title="Room settings - where the room stands on its question, the plan and its questions, the setup and the worklists"
      testId="director-line"
    >
      <div className="truncate text-xs text-gray-700 dark:text-gray-200">{head.gist}</div>
      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
        {head.chips.map((c, i) => (
          <span key={c.text} className={c.warn ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400'}>
            {i > 0 && '· '}
            {c.text}
          </span>
        ))}
        <span className="text-gray-400">
          {head.chips.length > 0 && '· '}
          <Door>details</Door>
        </span>
      </div>
    </HeaderRow>
  );
}

/** One card of the brief: a plain title, an optional note beside it, the body.
 *  Shared with the answer's slideout, so the two read as one system. */
export function Card({
  title,
  aside,
  tone = 'plain',
  children,
  testId,
}: {
  title: string;
  aside?: ReactNode;
  tone?: 'plain' | 'attention';
  children: ReactNode;
  testId?: string;
}) {
  const ring =
    tone === 'attention'
      ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';
  return (
    <section className={`rounded-xl border p-4 ${ring}`} data-testid={testId}>
      <header className="mb-2 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {aside && <div className="ml-auto text-xs text-gray-400">{aside}</div>}
      </header>
      {children}
    </section>
  );
}

/** One control in the "Steer the room" card: what it is, what it changes. */
export function SteerRow({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-medium text-gray-800 dark:text-gray-100">{title}</span>
        <span className="text-xs text-gray-400">{hint}</span>
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** The room brief, for the slideout. */
/** Who is being sent at what this round. Its own piece so the room tab can
 *  fold it under "How it got here". */
export function Worklists({
  assignments,
  personas,
  onDrop,
}: {
  assignments: BoardroomAssignment[];
  personas?: Map<number, { id: number; name: string }>;
  onDrop?: (assignment: BoardroomAssignment) => void;
}) {
  const live = groupAssignments(assignments);
  if (!live.length) return null;
  return (
    <Card title="Worklists" aside="who is being sent at what this round">
          <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700" data-testid="director-assignments">
            {live.map(a => (
              <li key={a.id} className="flex items-start gap-2 py-2 first:pt-0 last:pb-0 text-xs" data-testid={`assignment-${a.id}`}>
                <div className="min-w-0">
                  <span className={a.status === 'taken' ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500'}>
                    {assignmentLine(a)}
                    {a.persona_id && personas?.get(a.persona_id) && (
                      <span className="text-gray-400"> · taken by {personas.get(a.persona_id)!.name}</span>
                    )}
                    {a.status === 'pending' && <span className="text-gray-400"> · waiting</span>}
                  </span>
                  {/* WHY, for the reader only. It is never sent to the persona:
                      an explanation of the asking is not part of the asking. */}
                  {a.reason && <div className="mt-0.5 text-xs text-gray-400">{a.reason}</div>}
                </div>
                {onDrop && (
                  <button
                    type="button"
                    onClick={() => onDrop(a)}
                    aria-label={`Not that: ${seatRole(a.archetype)}`}
                    title="Put this worklist back - the seat goes on with its ordinary work"
                    className="ml-auto shrink-0 text-gray-400 hover:text-red-500"
                  >
                    not that
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
  );
}

export default function DirectorNote({
  run,
  assignments,
  rounds,
  plan: roomPlan,
  frame,
  frameWaiting = 0,
  steer,
  onDrop,
  personas,
  hideWorklists = false,
}: {
  /** Leave the worklists out: the room tab shows them folded, elsewhere. */
  hideWorklists?: boolean;
  /** The dig running now, or the last one. */
  run: BoardroomRun | null;
  assignments: BoardroomAssignment[];
  /** The cap, for the "n of N rounds" line. */
  rounds: number;
  /** The room's latest plan, when the run has none of its own. */
  plan?: BoardroomResearchPlan | null;
  /** The frame card's body — edited and decided on in place. */
  frame?: ReactNode;
  /** Frame proposals waiting on you: the frame card moves to the top. */
  frameWaiting?: number;
  /** The "Steer the room" card's body: the focus, the next dig, who reads it. */
  steer?: ReactNode;
  /** Not that: put a worklist back rather than letting it run. */
  onDrop?: (assignment: BoardroomAssignment) => void;
  personas?: Map<number, { id: number; name: string }>;
}) {
  const note = run?.director_note ?? null;
  const plan = readyPlan(run, roomPlan);
  const tally = planTally(plan);
  const live = groupAssignments(assignments);
  if (!note && !plan && (hideWorklists || live.length === 0) && !frame && !steer) return null;
  const stale = noteIsStale(note, run);
  const gap = gapWords(note);
  const label = roundsLabel(run, rounds);
  const digging = run?.status === 'RUNNING';

  const frameCard = frame ? (
    <Card
      title="The setup"
      aside={frameWaiting ? <span className="font-medium text-amber-700 dark:text-amber-300">a change is waiting for you</span> : 'what this market is and how it is sorted'}
      tone={frameWaiting ? 'attention' : 'plain'}
      testId="brief-frame"
    >
      {frame}
    </Card>
  ) : null;

  return (
    <div className="flex flex-col gap-3" data-testid="director-note">
      {/* A DECISION FIRST: the one card that needs you goes to the top. */}
      {frameWaiting > 0 && frameCard}

      {(note?.state || gap) && (
        <Card
          title={digging ? 'Right now' : 'Where the last dig left it'}
          aside={
            <>
              {label}
              {/* A note written before the last turns landed describes a board
                  that has moved: say so rather than presenting it as current. */}
              {stale && (
                <span className="ml-1 italic" data-testid="director-stale">
                  · from earlier in the dig
                </span>
              )}
            </>
          }
        >
          {note?.state && (
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200" data-testid="director-state">
              {note.state}
            </p>
          )}
          {gap && (
            <p
              className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
              data-testid="director-gap"
            >
              {gap}
            </p>
          )}
        </Card>
      )}

      {plan && (
        <Card title="What this room is trying to learn" aside={tally ? `opened with ${tally}` : undefined} testId="director-plan">
          {plan.summary && <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{plan.summary}</p>}
          {(plan.questions?.length ?? 0) > 0 && (
            <>
              <ol className="mt-3 flex flex-col gap-2">
                {plan.questions!.map((q, i) => (
                  <li key={q} className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-200">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{q}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-xs text-gray-400">Every persona reads these questions in its brief, every turn.</p>
            </>
          )}
        </Card>
      )}

      {/* WHERE THE ROOM IS POINTED, after what it is doing and what it is
          trying to learn — so you steer having read both. */}
      {steer && (
        <Card title="Steer the room" testId="brief-steer">
          {steer}
        </Card>
      )}

      {live.length > 0 && !hideWorklists && (
        <Worklists assignments={assignments} personas={personas} onDrop={onDrop} />
      )}

      {/* Reference, not news: when nothing is waiting, the frame sits last. */}
      {frameWaiting === 0 && frameCard}
    </div>
  );
}
